// ============================================================================
// NOTES INDEX (FSA-1)
// ============================================================================
// List view for the NOTES_BUNDLE report — one row per note schedule with
// note number, title, and a "Disclosures present" indicator. Clicking a
// row drills into NoteDetailView.

import React, { useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type {
  TemplateReportNode,
  TemplateScheduleNode,
} from 'api/types/fsa/template.types';
import {
  parseDisclosureRules,
  parseAdditionalDisclosures,
} from 'api/types/fsa/template.types';
import { DISCLOSURE_DEFINITIONS } from './disclosureDefinitions';

interface NotesIndexProps {
  reportNode: TemplateReportNode;
  /** Click handler — receives the schedule (note) clicked. */
  onOpenNote: (scheduleNode: TemplateScheduleNode) => void;
}

const NotesIndex: React.FC<NotesIndexProps> = ({ reportNode, onOpenNote }) => {
  const { report, schedules } = reportNode;

  // Sort by template display_order; engagement-time numbering is computed
  // separately at FSA-3 render based on which notes contain non-zero data.
  const sorted = useMemo(() => {
    return [...schedules].sort((a, b) => {
      return (a.schedule.displayOrder ?? 0) - (b.schedule.displayOrder ?? 0);
    });
  }, [schedules]);

  return (
    <div style={{ padding: '0.5rem 0' }}>
      <header style={headerStyle}>
        <h2 style={titleStyle}>{report.name}</h2>
        <p style={subtitleStyle}>
          {schedules.length} note{schedules.length === 1 ? '' : 's'} attached to balance sheet
          and profit &amp; loss line items. Click a note to see its breakdown and the disclosures
          required when reporting that note.
        </p>
        <p style={hintStyle}>
          <IconSystem.info size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />
          Note numbers on the printed Balance Sheet / P&amp;L are assigned per engagement and only
          appear next to lines that have data. The order below is the template's default presentation order.
        </p>
      </header>

      <div style={notesListStyle}>
        {sorted.map((sn) => (
          <NoteRow key={sn.schedule.id} node={sn} onClick={() => onOpenNote(sn)} />
        ))}
      </div>
    </div>
  );
};

export default NotesIndex;

// ============================================================================
// NOTE ROW
// ============================================================================

const NoteRow: React.FC<{
  node: TemplateScheduleNode;
  onClick: () => void;
}> = ({ node, onClick }) => {
  const { schedule, classes } = node;

  // Count: leaf classes (the line items inside this note) + disclosures
  // present from both sources (schedule-level additional_disclosures and
  // class-level disclosure_rules flags).
  const counts = useMemo(() => {
    let leafCount = 0;
    const seen = new Set<string>();
    // Schedule-level disclosures count first.
    for (const d of parseAdditionalDisclosures(schedule)) {
      if (!seen.has(`s:${d.code}`)) seen.add(`s:${d.code}`);
    }
    // Walk classes (direct schedule_id after fix 022 — no sections layer).
    const walk = (cls: any[]) => {
      for (const c of cls) {
        if (!c.children || c.children.length === 0) leafCount++;
        const rules = parseDisclosureRules(c);
        for (const k of Object.keys(rules)) {
          if (rules[k] === true && DISCLOSURE_DEFINITIONS[k] && !seen.has(`c:${k}`)) {
            seen.add(`c:${k}`);
          }
        }
        if (c.children?.length) walk(c.children);
      }
    };
    walk(classes as any[]);
    return { leafCount, disclosureCount: seen.size };
  }, [schedule, classes]);

  return (
    <button
      type="button"
      onClick={onClick}
      style={rowButtonStyle}
      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-secondary)')}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--bg-primary)')}
    >
      <div style={noteIconBadgeStyle}>
        <IconSystem.file size={16} />
      </div>
      <div style={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
        <div style={noteNameStyle}>{schedule.name}</div>
        {schedule.description && (
          <div style={noteDescStyle}>{schedule.description}</div>
        )}
        <div style={metaRowStyle}>
          <span>
            {counts.leafCount} {counts.leafCount === 1 ? 'line item' : 'line items'}
          </span>
          {counts.disclosureCount > 0 && (
            <span style={disclosureChipStyle}>
              <IconSystem.info size={11} style={{ marginRight: 3, verticalAlign: 'middle' }} />
              {counts.disclosureCount} disclosure{counts.disclosureCount === 1 ? '' : 's'}
            </span>
          )}
        </div>
      </div>
      <div style={chevronStyle}>
        <IconSystem.chevronRight size={16} />
      </div>
    </button>
  );
};

// ============================================================================
// STYLES
// ============================================================================

const headerStyle: React.CSSProperties = {
  marginBottom: '0.75rem',
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '1.05rem',
  fontWeight: 700,
  color: 'var(--text-primary)',
};

const subtitleStyle: React.CSSProperties = {
  margin: '0.25rem 0 0 0',
  fontSize: '0.8125rem',
  color: 'var(--text-secondary)',
  lineHeight: 1.5,
};

const notesListStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  border: '1px solid var(--border-color)',
  borderRadius: 6,
  overflow: 'hidden',
};

const rowButtonStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem',
  padding: '0.65rem 0.85rem',
  background: 'var(--bg-primary)',
  border: 'none',
  borderBottom: '1px solid var(--border-color)',
  cursor: 'pointer',
  width: '100%',
  textAlign: 'left',
  transition: 'background 0.1s ease',
  color: 'var(--text-primary)',
};

const noteIconBadgeStyle: React.CSSProperties = {
  flexShrink: 0,
  width: 32,
  height: 32,
  borderRadius: 4,
  background: '#dbeafe',
  color: '#1e3a8a',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const hintStyle: React.CSSProperties = {
  margin: '0.5rem 0 0 0',
  fontSize: '0.7rem',
  color: 'var(--text-secondary)',
  fontStyle: 'italic',
  padding: '0.4rem 0.6rem',
  background: 'var(--bg-secondary)',
  borderLeft: '3px solid #2563eb',
  borderRadius: 4,
  lineHeight: 1.4,
};

const noteNameStyle: React.CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'var(--text-primary)',
};

const noteDescStyle: React.CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--text-secondary)',
  marginTop: 2,
};

const metaRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: '0.75rem',
  marginTop: 4,
  fontSize: '0.7rem',
  color: 'var(--text-secondary)',
  alignItems: 'center',
};

const disclosureChipStyle: React.CSSProperties = {
  fontSize: '0.65rem',
  fontWeight: 600,
  padding: '1px 6px',
  borderRadius: 999,
  background: '#f5f3ff',
  color: '#5b21b6',
  display: 'inline-flex',
  alignItems: 'center',
};

const chevronStyle: React.CSSProperties = {
  flexShrink: 0,
  color: 'var(--text-secondary)',
  display: 'flex',
  alignItems: 'center',
};
